import { useCallback, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { attachJob, selectJobByCourse, type JobEntry } from './jobsSlice'
import { useResumeJobMutation } from './studioApi'
import { parseStartError, type StartFailure } from './lib/parseStartError'
import { progressFromResult } from './lib/jobProgress'
import { failedReasonText } from './lib/failedReasonText'
import type { StudioJob, StudioJobType } from './studioTypes'

/**
 * Продолжение прерванного прогона. Продолжение — НОВАЯ джоба, поэтому хук не
 * просто дёргает ручку: он перецепляет реестр курса на неё и снимает снимок
 * родителя. Без снимка автор теряет числа прерванного прогона — у продолжения
 * свой `result`, и взять их будет неоткуда.
 *
 * `continuesChat: true` — это тот же разговор, продолженный сервером, а не
 * новый прогон. Второй (и единственный кроме цепочки перевода) законный
 * вызывающий этого флага; везде ещё перецепление стирает историю чата.
 *
 * Отказ `already_resumed` — не ошибка: прогон уже продолжен, и в теле лежит id
 * продолжения. Перецепляемся на него тем же способом, только без снимка: числа
 * родителя автор в этом сценарии уже видел.
 */
export const useResumeJob = (courseId: number) => {
  const dispatch = useAppDispatch()
  // Читаем ТЕКУЩУЮ запись реестра, а не только параметры вызова: у неё есть
  // `translationChain` — факт, что продолжаемая джоба сама была картиночной
  // половиной цепочки «перевод → картинки» (§7.4). Флаг живёт НЕ в `previous`
  // (перезаписывается целиком ниже), поэтому перенести его можно только
  // отсюда, до перезаписи.
  const currentEntry = useAppSelector((s) => selectJobByCourse(s, courseId))
  const [resumeJob, { isLoading }] = useResumeJobMutation()
  const [failure, setFailure] = useState<StartFailure | null>(null)

  const attachResumed = useCallback(
    (
      jobId: number,
      type: StudioJobType,
      extra?: { total?: number | null; previous?: JobEntry['previous'] },
    ) => {
      dispatch(
        attachJob({
          courseId,
          entry: {
            jobId,
            type,
            startedAt: Date.now(),
            // Параметры продолжения задал сервер, а не автор: тело пустое.
            params: null,
            total: extra?.total ?? null,
            previous: extra?.previous,
            translationChain: currentEntry?.translationChain,
          },
          continuesChat: true,
        }),
      )
    },
    [dispatch, courseId, currentEntry?.translationChain],
  )

  const resume = useCallback(
    async (parent: StudioJob): Promise<boolean> => {
      setFailure(null)
      try {
        const started = await resumeJob(parent.id).unwrap()
        attachResumed(started.job_id, started.job_type, {
          total: started.plan_size,
          previous: {
            jobId: parent.id,
            type: parent.type,
            kind: 'resume',
            progress: progressFromResult(parent.type, parent.result),
            skipped: parent.result?.skipped,
            skipped_codes: parent.result?.skipped_codes,
            reason: failedReasonText(parent.error_code, parent.error_message),
          },
        })
        return true
      } catch (error) {
        const parsed = parseStartError(error)
        if (parsed.kind === 'already_resumed') {
          attachResumed(parsed.jobId, parent.type)
          return true
        }
        setFailure(parsed)
        return false
      }
    },
    [resumeJob, attachResumed],
  )

  /**
   * §8: `reason: "already_resumed"` в подсказке `resume` значит «кнопки уже
   * нет, есть готовое продолжение» — `hint.job_id` называет его прямо.
   * Перецепляемся на него без сетевого похода: звать ручку заново означало
   * бы заведомо получить тот же `409` и решить его тем же путём — честнее
   * сразу показать то, что сервер уже назвал.
   */
  const showResumed = useCallback(
    (jobId: number, type: StudioJobType) => {
      setFailure(null)
      attachResumed(jobId, type)
    },
    [attachResumed],
  )

  return { resume, isResuming: isLoading, failure, reset: () => setFailure(null), showResumed }
}
